import Button, { ButtonType } from "components/Elements/Buttonv2";
import Input from "components/Elements/Inputv2";
import searchIconImage from "./svg/search-icon.svg";
import threeDotsIconImage from "./svg/three-dots-icon.svg";
import emptyDataImage from "./svg/empty-data.svg";
import React, { Fragment, useEffect, useState } from "react";
import Table from "components/Tablev2";
import { format } from "date-fns";
import { Menu, Transition } from "@headlessui/react";
import ApiService from "services/api.service";
import { useNavigate } from "react-router-dom";
import Pagination from "components/Pagination";
import { toast } from "react-toastify";
import KeyIcon from "@heroicons/react/24/solid/KeyIcon";
import { useDebounce } from "react-use";
import {
  keepPreviousData,
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import sortAscChevronsImage from "./svg/sort-asc-chevrons.svg";
import sortDescChevronsImage from "./svg/sort-desc-chevrons.svg";
import sortNoneChevronsImage from "./svg/sort-none-chevrons.svg";
import NamePersonModal from "./Modals/NamePersonModal";
import AddToGroupModal from "./Modals/AddToGroupModal";
import AutoComplete from "components/AutoCompletev2";
import type { Attribute } from "pages/PeopleSettings/PeopleSettings";
import {
  getContactAttributeLabel,
  getContactAttributeTypeName,
  getFallbackContactAttributeType,
  mergeDefaultContactAttributes,
} from "utils/contactAttributes";
import { prefetchContactAttributeSettings } from "utils/contactAttributeQueries";

export interface PeopleRowData {
  id: string;
  email?: string;
  phone?: string;
  firstName?: string | null;
  lastName?: string | null;
  agency?: string | null;
  sourceType?: string;
  sourceLabel?: string;
  imported?: boolean;
  importedLabel?: string;
  importFileName?: string | null;
  leadId?: string | number | null;
  companyId?: string | number | null;
  policyStatus?: string | null;
  createdAt: string;
  updatedAt?: string;
  [key: string]: any;
}

const SortProperty = {
  CONTACT_NAME: "contactName",
  EMAIL: "email",
  PHONE: "phone",
  SOURCE: "source",
  IMPORTED: "imported",
  LEAD_ID: "leadId",
  COMPANY_ID: "companyId",
  POLICY_STATUS: "policyStatus",
  AGENCY: "agency",
  CREATED_AT: "createdAt",
  UPDATED_AT: "updatedAt",
} as const;

type SortPropertyValue =
  | (typeof SortProperty)[keyof typeof SortProperty]
  | `attribute:${string}`;

enum SortType {
  ASC = "asc",
  DESC = "desc",
}

interface SortOptions {
  sortBy: SortPropertyValue;
  sortType: SortType;
}

const CONTACTS_ITEMS_PER_PAGE = 100;

export interface ContactsQueryParams {
  page: number;
  search: string;
  searchKey: string;
  searchValue: string;
  sortBy: SortPropertyValue;
  sortType: SortType;
}

export const DEFAULT_CONTACTS_QUERY_PARAMS: ContactsQueryParams = {
  page: 1,
  search: "",
  searchKey: "",
  searchValue: "",
  sortBy: SortProperty.CREATED_AT,
  sortType: SortType.DESC,
};

interface ContactSourceStat {
  sourceType: string;
  sourceLabel: string;
  count: number;
}

interface ContactSourceStatsResponse {
  total: number;
  sources: ContactSourceStat[];
}

// Single source of truth for the customers list request — used by both the
// page and the app-entry prefetch so they resolve to the same cache entry.
export const buildContactsQuery = (params: ContactsQueryParams) => ({
  queryKey: [
    "contacts",
    {
      page: params.page,
      search: params.search,
      searchKey: params.searchKey,
      searchValue: params.searchValue,
      sortBy: params.sortBy,
      sortType: params.sortType,
    },
  ],
  queryFn: async () => {
    const query = new URLSearchParams({
      take: CONTACTS_ITEMS_PER_PAGE.toString(),
      skip: ((params.page - 1) * CONTACTS_ITEMS_PER_PAGE).toString(),
      search: params.search,
      searchKey: params.searchKey,
      searchValue: params.searchValue,
      orderBy: params.sortBy,
      orderType: params.sortType,
    });
    const {
      data: { data: results, totalPages, pkName },
    } = await ApiService.get<{
      data: PeopleRowData[];
      totalPages: number;
      pkName?: string;
    }>({
      url: `/contacts?${query.toString()}`,
    });

    const rows: PeopleRowData[] = results.map((person) => ({
      id: person.id,
      email: person.email,
      phone: person.phone,
      firstName: person.firstName ?? null,
      lastName: person.lastName ?? null,
      agency: person.agency ?? null,
      sourceType: person.sourceType,
      sourceLabel: person.sourceLabel,
      imported: person.imported,
      importedLabel: person.importedLabel,
      importFileName: person.importFileName ?? null,
      leadId: person.leadId ?? null,
      companyId: person.companyId ?? null,
      policyStatus: person.policyStatus ?? null,
      createdAt: person.createdAt,
      updatedAt: person.updatedAt,
      ...(pkName ? { [pkName]: person[pkName] } : {}),
    }));

    return { rows, totalPages, pkName };
  },
});

const buildContactSourceStatsQuery = (
  params: Pick<ContactsQueryParams, "search" | "searchKey" | "searchValue">
) => ({
  queryKey: [
    "contacts-source-stats",
    {
      search: params.search,
      searchKey: params.searchKey,
      searchValue: params.searchValue,
    },
  ],
  queryFn: async () => {
    const query = new URLSearchParams({
      search: params.search,
      searchKey: params.searchKey,
      searchValue: params.searchValue,
    });
    const { data } = await ApiService.get<ContactSourceStatsResponse>({
      url: `/contacts/source-stats?${query.toString()}`,
    });

    return {
      total: Number(data.total ?? 0),
      sources: (data.sources ?? []).map((source) => ({
        sourceType: source.sourceType,
        sourceLabel: source.sourceLabel,
        count: Number(source.count ?? 0),
      })),
    };
  },
});

export const prefetchContacts = (queryClient: QueryClient) =>
  queryClient.prefetchQuery(buildContactsQuery(DEFAULT_CONTACTS_QUERY_PARAMS));

const PeopleTablev2 = () => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [isNamePersonModalOpen, setIsNamePersonModalOpen] = useState(false);
  // Contact ids picked via the row checkboxes. Kept by id so a selection
  // survives paging and filtering rather than resetting each page.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isAddToGroupOpen, setIsAddToGroupOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [debouncedSearchKey, setDebouncedSearchKey] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [sortOptions, setSortOptions] = useState<SortOptions>({
    sortBy: SortProperty.CREATED_AT,
    sortType: SortType.DESC,
  });
  const [possibleKeys, setPossibleKeys] = useState<Attribute[]>([]);
  const [keysQuery, setKeysQuery] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  useDebounce(
    () => {
      setDebouncedSearchText(searchText);
    },
    350,
    [searchText]
  );

  // Debounce the search fields into the values the query uses so typing
  // doesn't fire a request per keystroke.
  useDebounce(
    () => {
      setDebouncedSearchKey(searchKey);
      setDebouncedSearchValue(searchValue);
    },
    500,
    [searchKey, searchValue]
  );

  const { data, isPending, isError, isSuccess } = useQuery({
    // Shared factory so the page and the app-entry prefetch hit the same key.
    ...buildContactsQuery({
      page: currentPage,
      search: debouncedSearchText,
      searchKey: debouncedSearchKey,
      searchValue: debouncedSearchValue,
      sortBy: sortOptions.sortBy,
      sortType: sortOptions.sortType,
    }),
    // Keep the current rows on screen while the next request runs, so paging,
    // filtering, and returning to the page never blank the table.
    placeholderData: keepPreviousData,
  });

  const { data: sourceStats, isPending: isSourceStatsPending } = useQuery({
    ...buildContactSourceStatsQuery({
      search: debouncedSearchText,
      searchKey: debouncedSearchKey,
      searchValue: debouncedSearchValue,
    }),
    placeholderData: keepPreviousData,
  });

  // The full, unfiltered source breakdown. Used as the stable set of source
  // cards so a search never makes a card vanish: with no active filter this
  // resolves to the same cache entry as the filtered query above (no extra
  // request), and while filtering it still lists every source category so we
  // can show 0 for the ones that don't match instead of hiding them.
  const { data: allSourceStats } = useQuery({
    ...buildContactSourceStatsQuery({
      search: "",
      searchKey: "",
      searchValue: "",
    }),
    placeholderData: keepPreviousData,
  });

  // Counts for the current search, looked up per source so the stable cards
  // can show the filtered number (or 0 when a source has no matches).
  const filteredCountByType = new Map(
    (sourceStats?.sources ?? []).map((s) => [s.sourceType, s.count] as const)
  );

  const rows = data?.rows ?? [];
  const pagesCount = data?.totalPages ?? 1;
  const pkKeyName = data?.pkName;

  // ----- row selection (for "Add to group") ---------------------------------
  const selectedSet = new Set(selectedIds);
  const pageIds = rows.map((row) => row.id);
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedSet.has(id));

  const toggleOne = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // Select-all toggles only the current page: select every page row, or, if the
  // page is already fully selected, deselect just this page's rows.
  const togglePage = () =>
    setSelectedIds((prev) => {
      if (allPageSelected) return prev.filter((id) => !pageIds.includes(id));
      const merged = new Set(prev);
      pageIds.forEach((id) => merged.add(id));
      return Array.from(merged);
    });

  const clearSelection = () => setSelectedIds([]);

  useEffect(() => {
    if (isError) toast.error("Failed to load data");
  }, [isError]);

  useEffect(() => {
    void prefetchContactAttributeSettings(queryClient);
  }, [queryClient]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    sortOptions,
    debouncedSearchText,
    debouncedSearchKey,
    debouncedSearchValue,
  ]);

  const loadPossibleKeys = async (q: string) => {
    const { data: attributes } = await ApiService.get<Attribute[]>({
      url: `/contacts/possible-attributes?key=${encodeURIComponent(q)}`,
    });
    setPossibleKeys(mergeDefaultContactAttributes(attributes, [], q));
  };

  useDebounce(
    () => {
      loadPossibleKeys(keysQuery);
    },
    100,
    [keysQuery]
  );

  type ContactsCache = {
    rows: PeopleRowData[];
    totalPages: number;
    pkName?: string;
  };

  // Delete a contact with an optimistic cache update: drop the row from every
  // cached page right away, roll back if the request fails, no list refetch.
  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      ApiService.post({ url: "/contacts/delete/" + id }),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ["contacts"] });
      const snapshot = queryClient.getQueriesData<ContactsCache>({
        queryKey: ["contacts"],
      });
      queryClient.setQueriesData<ContactsCache>(
        { queryKey: ["contacts"] },
        (old) =>
          old ? { ...old, rows: old.rows.filter((r) => r.id !== id) } : old
      );
      return { snapshot };
    },
    onError: (_err, _id, context) => {
      context?.snapshot.forEach(([key, value]) =>
        queryClient.setQueryData(key, value)
      );
      toast.error("Failed to delete user");
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["contacts-source-stats"],
      });
    },
  });

  const handleDeletePerson = (id: string) => {
    if (!window.confirm("Delete this contact? This cannot be undone.")) return;
    deleteMutation.mutate(id);
  };

  const handleCloseNamePersonModal = () => {
    setIsNamePersonModalOpen(false);
    void queryClient.invalidateQueries({ queryKey: ["contacts"] });
    void queryClient.invalidateQueries({ queryKey: ["contacts-source-stats"] });
  };

  const handleSort = (sortBy: SortPropertyValue) => {
    setSortOptions((current) => {
      if (current.sortBy !== sortBy) {
        return { sortBy, sortType: SortType.ASC };
      }

      return {
        sortBy,
        sortType:
          current.sortType === SortType.ASC ? SortType.DESC : SortType.ASC,
      };
    });
  };

  const sortIcon = (sortBy: SortPropertyValue) => {
    if (sortOptions.sortBy !== sortBy) return sortNoneChevronsImage;
    return sortOptions.sortType === SortType.ASC
      ? sortAscChevronsImage
      : sortDescChevronsImage;
  };

  const sortableHeading = (
    label: string,
    sortBy: SortPropertyValue,
    testId?: string
  ) => (
    <button
      type="button"
      data-testid={testId}
      className="px-5 py-[10px] select-none flex gap-[4px] items-center cursor-pointer whitespace-nowrap"
      onClick={() => handleSort(sortBy)}
    >
      <span>{label}</span>
      <img src={sortIcon(sortBy)} alt="" />
    </button>
  );

  const formatDateTime = (value?: string | null) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return format(date, "MM/dd/yyyy HH:mm");
  };

  const contactName = (row: PeopleRowData) =>
    [row.firstName, row.lastName].filter(Boolean).join(" ");

  const sourceBadgeClass = (sourceType?: string) => {
    switch (sourceType) {
      case "leadstats":
        return "bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]";
      case "csv_upload":
        return "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]";
      case "manual":
        return "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]";
      case "api_event":
      case "upsert":
      case "event":
        return "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]";
      default:
        return "bg-themed-background text-themed-textSecondary border-themed-border";
    }
  };

  const renderSource = (row: PeopleRowData) => (
    <span
      className={`inline-flex items-center rounded border px-2 py-0.5 text-[12px] leading-[18px] whitespace-nowrap ${sourceBadgeClass(
        row.sourceType
      )}`}
      title={row.importFileName || undefined}
    >
      {row.sourceLabel || "Legacy / Manual"}
    </span>
  );

  const renderImported = (row: PeopleRowData) => (
    <span
      className={`inline-flex items-center rounded border px-2 py-0.5 text-[12px] leading-[18px] whitespace-nowrap ${
        row.imported
          ? "bg-[#22C55E]/15 text-[#16A34A] border-[#22C55E]/40"
          : "bg-themed-background text-themed-textSecondary border-themed-border"
      }`}
    >
      {row.imported ? "Yes" : "No"}
    </span>
  );

  const formatCount = (value?: number) =>
    new Intl.NumberFormat().format(value ?? 0);
  const hasActiveFilter = Boolean(
    debouncedSearchText || debouncedSearchKey || debouncedSearchValue
  );
  const selectedSearchAttribute = possibleKeys.find(
    (attribute) => attribute.name === searchKey
  ) || {
    name: searchKey,
    attribute_type: getFallbackContactAttributeType(),
  };
  const attributePickerLabel = (attribute: Attribute) => {
    const label = getContactAttributeLabel(attribute.name);
    const type = getContactAttributeTypeName(attribute);
    return label === attribute.name
      ? `${attribute.name} - ${type}`
      : `${label} (${attribute.name}) - ${type}`;
  };
  const clearFieldFilter = () => {
    setSearchKey("");
    setSearchValue("");
    setKeysQuery("");
  };

  return (
    <div className="p-5 flex flex-col gap-5 font-inter font-normal text-[14px] text-themed-text leading-[22px]">
      <div className="flex justify-between items-center">
        <div className="text-[20px] font-semibold leading-[28px]">Contacts</div>

        <div className="flex items-center gap-[10px]">
          <Button
            type={ButtonType.SECONDARY}
            id="people-settings"
            onClick={() => {
              void prefetchContactAttributeSettings(queryClient);
              navigate("/people/setting");
            }}
          >
            Manage attributes
          </Button>
          <Button
            type={ButtonType.SECONDARY}
            onClick={() => {
              navigate("/people/import");
            }}
            id="import-customer-button"
          >
            Import Contacts via CSV
          </Button>
          <Button
            type={ButtonType.PRIMARY}
            onClick={() => setIsNamePersonModalOpen(true)}
            id="create-customer-button"
          >
            Create New Contact
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <div className="min-h-[96px] rounded-lg border border-themed-border bg-themed-card p-4">
          <div className="text-[12px] font-semibold uppercase text-themed-textSecondary">
            Total people
          </div>
          <div className="mt-2 text-[28px] font-semibold leading-[34px] text-themed-text">
            {isSourceStatsPending && !sourceStats
              ? "..."
              : formatCount(sourceStats?.total)}
          </div>
          <div className="mt-1 text-[12px] text-themed-textSecondary">
            {hasActiveFilter ? "Matching current filters" : "All contacts"}
          </div>
        </div>

        {(allSourceStats?.sources ?? []).map((source) => (
          <div
            key={source.sourceType}
            className="min-h-[96px] rounded-lg border border-themed-border bg-themed-card p-4"
          >
            <div className="flex min-w-0 items-center gap-2">
              <span
                className={`h-2.5 w-2.5 flex-shrink-0 rounded-full border ${sourceBadgeClass(
                  source.sourceType
                )}`}
              />
              <div
                className="truncate text-[12px] font-semibold uppercase text-themed-textSecondary"
                title={source.sourceLabel}
              >
                {source.sourceLabel}
              </div>
            </div>
            <div className="mt-2 text-[28px] font-semibold leading-[34px] text-themed-text">
              {formatCount(filteredCountByType.get(source.sourceType) ?? 0)}
            </div>
            <div className="mt-1 text-[12px] text-themed-textSecondary">
              By source
            </div>
          </div>
        ))}
      </div>

      <div className="p-5 bg-themed-card rounded-lg flex flex-col gap-5">
        {rows.length === 0 &&
        searchText === "" &&
        searchKey === "" &&
        searchValue === "" &&
        isSuccess ? (
          <div className="w-full h-[300px] flex items-center justify-center select-none">
            <div className="flex flex-col items-center gap-5">
              <img src={emptyDataImage} />

              <div className="font-inter text-[16px] font-semibold leading-[24px] text-themed-textSecondary">
                Import or Create Contacts to Get Started
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="w-full lg:max-w-[420px]">
                <Input
                  value={searchText}
                  onChange={setSearchText}
                  placeholder="Search contacts"
                  showClearButton
                />
              </div>

              <div className="flex w-full flex-wrap gap-[10px] items-center justify-end lg:w-auto">
                <div className="flex items-center gap-2 text-[13px] font-medium text-themed-textSecondary">
                  <img
                    src={searchIconImage}
                    alt=""
                    className="search-icon w-4 h-4"
                  />
                  Field filter
                </div>
                <div className="w-full sm:w-[260px]">
                  <AutoComplete<Attribute>
                    value={selectedSearchAttribute}
                    onQueryChange={(q) => {
                      setSearchKey(q);
                      setKeysQuery(q);
                    }}
                    onSelect={(attribute) => {
                      setSearchKey(attribute.name);
                      setKeysQuery(attribute.name);
                    }}
                    includedItems={{
                      type: "getter",
                      items: possibleKeys,
                    }}
                    retrieveLabel={attributePickerLabel}
                    getKey={(attribute) => attribute.name}
                    placeholder="Attribute"
                  />
                </div>

                <div className="w-full sm:w-[220px]">
                  <Input
                    value={searchValue}
                    onChange={setSearchValue}
                    placeholder="Field value"
                    showClearButton
                  />
                </div>

                <Button type={ButtonType.LINK} onClick={clearFieldFilter}>
                  Clear
                </Button>
              </div>
            </div>

            {selectedIds.length > 0 && (
              <div className="flex flex-wrap items-center gap-[14px] px-4 py-[10px] rounded-md bg-[#4338CA] border border-[#4338CA] text-white">
                <span className="font-semibold text-white">
                  {selectedIds.length} selected
                </span>
                <Button
                  type={ButtonType.PRIMARY}
                  onClick={() => setIsAddToGroupOpen(true)}
                  id="add-to-group-button"
                >
                  Add to group
                </Button>
                <button
                  type="button"
                  className="underline text-white"
                  onClick={clearSelection}
                >
                  Clear selection
                </button>
              </div>
            )}

            <div className="w-full overflow-x-auto">
              <Table
                className="min-w-[1480px] w-full"
                isLoading={isPending}
                onRowClick={(i) => navigate(`/person/${rows[i].id}`)}
                headings={[
                  <div
                    className="px-2 select-none"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      aria-label="Select all on this page"
                      className="h-4 w-4 cursor-pointer text-[#6366F1] focus:outline-none focus:ring-0 focus:ring-offset-0"
                      checked={allPageSelected}
                      onChange={togglePage}
                    />
                  </div>,
                  sortableHeading(
                    "Contact Name",
                    SortProperty.CONTACT_NAME,
                    "name-header"
                  ),
                  sortableHeading("Email", SortProperty.EMAIL, "email-header"),
                  sortableHeading("Phone", SortProperty.PHONE, "phone-header"),
                  sortableHeading(
                    "Source",
                    SortProperty.SOURCE,
                    "source-header"
                  ),
                  sortableHeading(
                    "Imported",
                    SortProperty.IMPORTED,
                    "imported-header"
                  ),
                  sortableHeading("Lead ID", SortProperty.LEAD_ID),
                  sortableHeading("Company", SortProperty.COMPANY_ID),
                  sortableHeading("Policy Status", SortProperty.POLICY_STATUS),
                  sortableHeading(
                    "Agency",
                    SortProperty.AGENCY,
                    "agency-header"
                  ),
                  ...(pkKeyName
                    ? [
                        <button
                          type="button"
                          className="px-5 py-[10px] select-none flex gap-[4px] items-center cursor-pointer whitespace-nowrap"
                          data-testid="pk-header"
                          onClick={() =>
                            handleSort(
                              `attribute:${pkKeyName}` as SortPropertyValue
                            )
                          }
                        >
                          <KeyIcon className="max-w-[12px] min-w-[12px] max-h-[12px] min-h-[12px]" />
                          <span>{pkKeyName}</span>
                          <img
                            src={sortIcon(
                              `attribute:${pkKeyName}` as SortPropertyValue
                            )}
                            alt=""
                          />
                        </button>,
                      ]
                    : []),
                  sortableHeading(
                    "Created",
                    SortProperty.CREATED_AT,
                    "sort-header"
                  ),
                  sortableHeading("Updated", SortProperty.UPDATED_AT),
                  <div className="px-5 py-[10px] select-none"></div>,
                ]}
                rowsData={rows}
                rows={rows.map((row) => [
                  <div className="px-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label="Select contact"
                      className="h-4 w-4 cursor-pointer text-[#6366F1] focus:outline-none focus:ring-0 focus:ring-offset-0"
                      checked={selectedSet.has(row.id)}
                      onChange={() => toggleOne(row.id)}
                    />
                  </div>,
                  <div
                    className="min-w-[150px] max-w-[220px] truncate pl-5"
                    title={contactName(row)}
                  >
                    {contactName(row)}
                  </div>,
                  <div
                    className="min-w-[190px] max-w-[260px] truncate pl-5"
                    title={row.email ?? ""}
                  >
                    {row.email ?? ""}
                  </div>,
                  <div className="min-w-[130px] whitespace-nowrap">
                    {row.phone ?? ""}
                  </div>,
                  <div>{renderSource(row)}</div>,
                  <div className="pl-5">{renderImported(row)}</div>,
                  <div
                    className="min-w-[90px] max-w-[140px] truncate"
                    title={`${row.leadId ?? ""}`}
                  >
                    {row.leadId ?? ""}
                  </div>,
                  <div
                    className="min-w-[90px] max-w-[140px] truncate"
                    title={`${row.companyId ?? ""}`}
                  >
                    {row.companyId ?? ""}
                  </div>,
                  <div
                    className="min-w-[130px] max-w-[180px] truncate"
                    title={row.policyStatus ?? ""}
                  >
                    {row.policyStatus ?? ""}
                  </div>,
                  <div
                    className="min-w-[130px] max-w-[180px] truncate"
                    title={row.agency ?? ""}
                  >
                    {row.agency ?? ""}
                  </div>,
                  ...(pkKeyName
                    ? [
                        <div
                          className="min-w-[130px] max-w-[200px] truncate"
                          title={`${row[pkKeyName] ?? ""}`}
                        >
                          {row[pkKeyName] ?? ""}
                        </div>,
                      ]
                    : []),
                  <div className="min-w-[130px] whitespace-nowrap">
                    {formatDateTime(row.createdAt)}
                  </div>,
                  <div className="min-w-[130px] whitespace-nowrap">
                    {formatDateTime(row.updatedAt)}
                  </div>,
                  <div
                    className="flex justify-end"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Menu as="div" className="relative">
                      <Menu.Button className="flex h-9 w-9 items-center justify-center rounded hover:bg-themed-background">
                        <img
                          className="row-actions-icon h-5 w-5"
                          src={threeDotsIconImage}
                          alt="Contact actions"
                        />
                      </Menu.Button>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute z-[120] right-0 origin-top-right w-[200px] py-[4px] rounded-sm bg-themed-card shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                className={`block w-full text-left py-[5px] px-[12px] text-[#F43F5E] ${
                                  active ? "bg-themed-background" : ""
                                }`}
                                onClick={() => handleDeletePerson(row.id)}
                              >
                                Delete
                              </button>
                            )}
                          </Menu.Item>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  </div>,
                ])}
              />
            </div>
          </>
        )}

        {pagesCount > 1 && (
          <div
            className="flex justify-center items-center"
            id="people-table-pagination"
          >
            <Pagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={pagesCount}
            />
          </div>
        )}
      </div>

      <NamePersonModal
        isOpen={isNamePersonModalOpen}
        onClose={handleCloseNamePersonModal}
      />

      <AddToGroupModal
        isOpen={isAddToGroupOpen}
        contactIds={selectedIds}
        onClose={() => setIsAddToGroupOpen(false)}
        onAdded={() => setSelectedIds([])}
      />
    </div>
  );
};

export default PeopleTablev2;
